import debounce from 'lodash/debounce';
import get from 'lodash/get';
import objectHash from 'object-hash';
import type { DOMAttributes, FormEvent, MouseEvent, SVGProps } from 'react';
import { useCallback, useContext, useEffect, useState } from 'react';
import type { GlobalError } from 'react-hook-form';
import { useFormContext } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import { CountryCodeEnum, LanguageSlugEnum } from '@/types/regionTypes';

import { Box } from '@/components/Box';
import { CheckoutSection } from '@/components/CheckoutSection';
import { Flex } from '@/components/Flex';
import type { InputProps } from '@/components/Input';
import { Input } from '@/components/Input';
import { InputErrorMessage } from '@/components/InputErrorMessage';
import * as IFS from '@/components/InputField/InputField.styles';
import LinkWithLocale from '@/components/LinkWithLocale/LinkWithLocale';
import type { RadioFieldProps } from '@/components/RadioField';
import { getTranslatedStoreValue } from '@/components/StoresList/utils';
import { Typography } from '@/components/Typography';
import { usePosition } from '@/hooks/usePosition';
import type { StoreHit } from '@/integrations/algolia/types';
import { DEBOUNCE_TIME_IN_MS } from '@/lib/const';
import { generateDataTestId } from '@/lib/tests';
import { formMessages } from '@/messages/forms';
import { DraftModeContext } from '@/providers/DraftModeProvider/DraftModeContext';
import { LocaleContext } from '@/providers/LocaleProvider';
import { useRegionProvider } from '@/providers/RegionProvider';

import { StoresSearch } from '../../helpers';

import * as S from './BoutiquePickup.styles';

export interface BoutiquePickupProps {
  clearErrorOpts?: string[];
  name: string;
  title?: string;
}

export const BoutiquePickup = ({
  clearErrorOpts = ['deliveryOption'],
  name,
  title,
}: BoutiquePickupProps) => {
  const HITS_PER_PAGE = 6;
  const draftMode = useContext(DraftModeContext);
  const { error: positionError, position } = usePosition();
  const aroundLatLng = position
    ? `${position.coords.latitude},${position.coords.longitude}`
    : undefined;
  const locale = useContext(LocaleContext);
  const { country } = useRegionProvider();
  const { clearErrors, formState, watch } = useFormContext();
  const error = get(formState.errors, name) as GlobalError;
  const { formatMessage } = useIntl();
  const [keyword, setKeyword] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const [boutiqueList, setBoutiqueList] = useState<Array<StoreHit>>([]);
  const clearErrorOptions = watch(clearErrorOpts);
  const [nbHits, setNbHits] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const storesSearch = async (query: string) => {
    try {
      setIsLoading(true);
      const { hits, nbHits } = await StoresSearch(
        query,
        locale,
        {
          hitsPerPage: HITS_PER_PAGE,
          facetFilters: [
            ['type:BOUTIQUE', 'type:FLAG_SHIP_BOUTIQUE'],
            ['operating_type:Internal', 'operating_type:External'],
            `country_code:${country.code}`,
            'offers_boutique_pickup: true',
          ],
          aroundLatLng: aroundLatLng,
          page: currentPage,
          aroundRadius: 'all',
        },
        draftMode,
      );
      setIsLoading(false);
      setNbHits(nbHits);
      if (currentPage === 0) {
        setBoutiqueList(hits);
      } else {
        setBoutiqueList(boutiqueList.concat(hits));
      }
    } catch (error) {
      setIsLoading(false);
      //TODO handle error if load failed
    }
  };
  useEffect(() => {
    if (position || positionError) {
      storesSearch(keyword);
    }
  }, [currentPage, keyword, position, positionError]);

  const loadMore = () => {
    setCurrentPage(currentPage + 1);
  };

  useEffect(() => {
    clearErrors(name);
  }, clearErrorOptions);
  // Find your closest boutique by keyword
  const handleInput = useCallback(
    debounce(async (value) => {
      setKeyword(value);
      setCurrentPage(0);
    }, DEBOUNCE_TIME_IN_MS),
    [],
  );

  return (
    <CheckoutSection>
      {title && <CheckoutSection.Header>{title}</CheckoutSection.Header>}
      <CheckoutSection.Summary>
        <BoutiquePickup.BoutiqueFind
          icon={S.Location}
          onChange={(e: FormEvent<HTMLInputElement>) => {
            handleInput(e.currentTarget.value);
          }}
          placeholder={formatMessage(formMessages.cityStreet)}
          {...generateDataTestId('input', 'findBoutique')}
        ></BoutiquePickup.BoutiqueFind>
        <InputErrorMessage error={error} />
        <S.BoutiqueList>
          {boutiqueList.map((node: StoreHit, index) => (
            <BoutiquePickup.BoutiqueRadio
              key={objectHash(node) + `-${index}`}
              name={name}
              node={node}
              value={node.store_id}
              {...generateDataTestId('radio', 'boutiqueCard')}
            ></BoutiquePickup.BoutiqueRadio>
          ))}
          {nbHits > boutiqueList.length && (
            <BoutiquePickup.BoutiqueMore
              isLoading={isLoading}
              onClick={loadMore}
            ></BoutiquePickup.BoutiqueMore>
          )}
        </S.BoutiqueList>
        {/* <InstantSearch searchClient={algoliaSearchClient}>
        <Configure hitsPerPage={HITS_PER_PAGE} aroundLatLng={aroundLatLng} />
      </InstantSearch> */}
      </CheckoutSection.Summary>
      {/* <CheckboxField name="preferredBoutique">Save it as preferred boutique</CheckboxField> */}
    </CheckoutSection>
  );
};

type BoutiqueFindProps = BoutiqueInputFieldProps;
export const BoutiqueFind = (props: BoutiqueFindProps) => {
  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
    }
  };
  return (
    <S.BoutiqueFindInputWrapper gridGap={2}>
      <Flex justifyContent='space-between'>
        <Typography>
          <FormattedMessage
            {...formMessages.findYourClosestBoutique}
          ></FormattedMessage>
        </Typography>
        {/* <Flex alignItems='center' gridGap={1}></Flex> */}
      </Flex>
      <BoutiquePickup.BoutiqueFieldInput
        {...props}
        onKeyDown={handleKeyDown}
      ></BoutiquePickup.BoutiqueFieldInput>
    </S.BoutiqueFindInputWrapper>
  );
};
interface BoutiqueInputFieldProps extends InputProps {
  icon?: React.ComponentType<SVGProps<SVGSVGElement>>;
}
export const BoutiqueFieldInput = ({
  placeholder,
  ...props
}: BoutiqueInputFieldProps) => {
  return (
    <IFS.Wrapper>
      {props.icon && <IFS.IconContainer as={props.icon} />}
      <Input {...props} placeholder={placeholder} />
    </IFS.Wrapper>
  );
};

interface BoutiqueRadioProps extends RadioFieldProps {
  node: StoreHit;
}
type OpenInterval = { end: string; start: string };
export interface OpeningHours {
  friday?: { openIntervals: Array<OpenInterval> };
  monday?: { openIntervals: Array<OpenInterval> };
  saturday?: { openIntervals: Array<OpenInterval> };
  sunday?: { openIntervals: Array<OpenInterval> };
  thursday?: { openIntervals: Array<OpenInterval> };
  tuesday?: { openIntervals: Array<OpenInterval> };
  wednesday?: { openIntervals: Array<OpenInterval> };
}
export type OpeningHourKeys = Array<keyof OpeningHours>;

export const openingHours: OpeningHourKeys = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
];
export const BoutiqueRadio = ({ node, ...props }: BoutiqueRadioProps) => {
  const { country, locale } = useRegionProvider();
  const langSlug = locale.slug;
  const hours = node.hours as OpeningHours;
  const showJPAddress =
    country.code === CountryCodeEnum.JP && langSlug === LanguageSlugEnum.ja;
  const street = getTranslatedStoreValue(node, 'street', langSlug);
  const street2 = getTranslatedStoreValue(node, 'street2', langSlug);
  const city = getTranslatedStoreValue(node, 'city', langSlug);
  const region = getTranslatedStoreValue(node, 'region', langSlug);
  const postalCode = getTranslatedStoreValue(node, 'postal_code', langSlug);
  const addressLines = showJPAddress
    ? [
        `${postalCode} ${region} ${city},`,
        street,
        ...(street2 ? [street2] : []),
      ]
    : [
        street,
        ...(street2 ? [street2] : []),
        `${city}, ${region} ${postalCode}`,
      ];
  return (
    <S.Wrapper>
      <S.Content {...props}>
        <Box>
          <Typography fontWeight='bold'>
            {getTranslatedStoreValue(node, 'name', langSlug)}
          </Typography>
          {addressLines.map((line, index) => (
            <Typography key={index}>{line}</Typography>
          ))}

          <Typography>
            <FormattedMessage {...formMessages.phone}></FormattedMessage>{' '}
            <LinkWithLocale href={'tel:' + node.phone}>
              {node.phone}
            </LinkWithLocale>
          </Typography>
          <Box mt={2}>
            <Typography fontWeight='bold'>
              <FormattedMessage
                {...formMessages.boutiqueHours}
              ></FormattedMessage>
            </Typography>
            <Box width={195}>
              {openingHours.map((item) => (
                <Flex key={item} justifyContent='space-between'>
                  <Typography>
                    <FormattedMessage
                      {...(formMessages[item] as any)}
                    ></FormattedMessage>
                  </Typography>
                  <Typography>
                    {hours &&
                      hours[item] &&
                      hours[item]?.openIntervals?.[0]?.start}{' '}
                    -{' '}
                    {hours &&
                      hours[item] &&
                      hours[item]?.openIntervals?.[0]?.end}
                  </Typography>
                </Flex>
              ))}
            </Box>
          </Box>
        </Box>
      </S.Content>
    </S.Wrapper>
  );
};

export interface BoutiqueMoreProps extends DOMAttributes<HTMLElement> {
  isLoading: boolean;
}
export const BoutiqueMore = ({
  isLoading,
  onClick,
  ...props
}: BoutiqueMoreProps) => {
  const handleClick = (event: MouseEvent<HTMLElement>) => {
    if (isLoading) return;
    onClick && onClick(event);
  };
  return (
    <S.Wrapper>
      <S.ContentMore
        as={Flex}
        isLoading={isLoading}
        onClick={handleClick}
        {...props}
      >
        <Typography fontWeight='bold' textDecoration='underline'>
          <FormattedMessage {...formMessages.seeMore}></FormattedMessage>
        </Typography>
      </S.ContentMore>
    </S.Wrapper>
  );
};

BoutiquePickup.BoutiqueRadio = BoutiqueRadio;
BoutiquePickup.BoutiqueMore = BoutiqueMore;
BoutiquePickup.BoutiqueFieldInput = BoutiqueFieldInput;
BoutiquePickup.BoutiqueFind = BoutiqueFind;

BoutiquePickup.displayName = 'BoutiquePickup';
